import React, { FC, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, CardFooter, CardHeader, Col, Row, Table } from 'reactstrap';
import {
  ActionButton,
  DataViewItem,
  DropdownItemProps,
  ElementHeader,
  PaginatedDataView,
  UserStateComponent,
} from '../../../components';
import { DateTime } from '../../../components/Date';
import { Surcharge, SurchargeOrderItem, SurchargeProduct } from '../../../models';
import { StringTranslator, useTranslation } from '../../../translations';
import { displayName, Fetcher, TableKVPair, useBoolState, useFetcher } from '../../../utils';
import { AssignModal } from './AssignModal';
import { surchargeRateText, SurchargeTitle } from './utils';

interface SurchargeContentProps {
  readonly surcharge: Surcharge;
  readonly refresh: () => void;
}

const SurchargeContent: FC<SurchargeContentProps> = ({ surcharge, refresh }) => {
  const typeText = <SurchargeTitle surcharge={surcharge} />;
  const rateText = surchargeRateText(surcharge);
  const { ts } = useTranslation();
  const [openModal, enableModal, disableModal] = useBoolState(false);
  const associatedProductsRef = useRef<(() => void) | null>(null);

  return (
    <>
      <ElementHeader
        icon={{ iconName: 'list' }}
        title={`${displayName(surcharge)} – ID ${surcharge.id}`}
      >
        <div className="badge-list compact">
          <span className="pretext">
            {typeText} {ts('common.of')}
            {rateText}
          </span>
          <Badge color={surcharge.deletedAt ? 'secondary' : 'success'}>
            {ts(surcharge.deletedAt ? 'common.deleted_archived' : 'active')}
          </Badge>
        </div>
      </ElementHeader>
      <Row className="surcharge-info justify-content-center">
        <Col lg={4} xs={12}>
          <Card>
            <CardHeader>{typeText}</CardHeader>
            <CardBody>
              <Table>
                <tbody>
                  <TableKVPair
                    displayName="surcharge.name_key"
                    name="name"
                    value={surcharge.name}
                  />
                  <TableKVPair displayName="type" name="type" value={typeText} />
                  <TableKVPair displayName="rate" name="rate" value={rateText} />
                  <TableKVPair
                    displayName="calculation_method"
                    name="calculation"
                    value={ts('common.exclusive')}
                  />
                  <TableKVPair
                    displayName="created"
                    name="createdAt"
                    value={ts('common.exclusive')}
                  />
                  {surcharge.deletedAt && (
                    <TableKVPair
                      displayName="archived"
                      name="deletedAt"
                      value={<DateTime value={surcharge.deletedAt} />}
                    />
                  )}
                </tbody>
              </Table>
            </CardBody>
            {!surcharge.deletedAt && (
              <CardFooter>
                <Row>
                  <Col lg={6} xs={12}>
                    <Button
                      block
                      className="assign-surcharge"
                      color="primary"
                      onClick={enableModal}
                    >
                      {ts('surcharge.assign_to_product')}
                    </Button>
                  </Col>
                  <Col lg={6} xs={12}>
                    <ActionButton
                      action={async () => {
                        await api.deleteSurcharge(surcharge.id);
                      }}
                      block
                      className="delete-surcharge"
                      color="danger"
                      disabled={surcharge.internal}
                      onSuccess={refresh}
                      outline
                    >
                      {ts(surcharge.internal ? 'cannot_be_deleted' : 'common.delete')} {typeText}
                    </ActionButton>
                  </Col>
                </Row>
              </CardFooter>
            )}
          </Card>
        </Col>
        <Col lg={6} xs={12}>
          <Card className="surcharge-associated-products mb-10">
            <CardHeader>{ts('raffles.editor.productSelector.associatedProducts')}</CardHeader>
            <PaginatedDataView<SurchargeProduct>
              dataFetcher={async (page) => await api.getSurchargeProductsById(surcharge.id, page)}
              pageSize={8}
              refreshRef={associatedProductsRef}
              renderer={({ createdAt, product }, refresh2) => (
                <DataViewItem
                  content={
                    <div className="badge-list compact">
                      {(product.deletedAt ?? surcharge.deletedAt) ? (
                        <Badge color="secondary">{ts('archived')}</Badge>
                      ) : (
                        <Badge color="success">{ts('assigned')}</Badge>
                      )}
                    </div>
                  }
                  dropdownItems={getProductDropdownOptions(ts, surcharge, product.id, refresh2)}
                  id={`product${product.id}`}
                  rightElement={<DateTime value={createdAt} />}
                  subtitle={`ID: ${product.id}`}
                  title={displayName(product)}
                />
              )}
            />
          </Card>
          <Card className="surcharge-associated-orderitems">
            <CardHeader>{ts('surcharge.associated_order_items')}</CardHeader>
            <PaginatedDataView<SurchargeOrderItem>
              dataFetcher={async (page) => await api.getSurchargeOrderItemsById(surcharge.id, page)}
              pageSize={8}
              renderer={({ createdAt, orderItem }) => (
                <DataViewItem
                  content={
                    <div className="badge-list compact">
                      {orderItem.order.status === 'paid' ? (
                        <Badge color="success">{ts('common.paid')}</Badge>
                      ) : (
                        <Badge color="secondary">{ts('common.unpaid')}</Badge>
                      )}
                    </div>
                  }
                  dropdownItems={[
                    {
                      className: 'view-order',
                      to: `/housekeeping/orders/${orderItem.orderId}`,
                      text: 'common.view_order',
                    },
                  ]}
                  id={`orderItem${orderItem.id}`}
                  rightElement={<DateTime value={createdAt} />}
                  subtitle={`Order #${orderItem.orderId}, OrderItem ID #${orderItem.id}`}
                  title={`${displayName(orderItem.product)} – ${orderItem.order.userId}`}
                />
              )}
            />
          </Card>
        </Col>
      </Row>
      <AssignModal
        onCancel={disableModal}
        onUpdate={() => {
          toast.success(ts('assigned_surcharge_successfully'));
          disableModal();
          if (associatedProductsRef.current) {
            associatedProductsRef.current();
          }
        }}
        open={openModal}
        surchargeId={surcharge.id}
      />
    </>
  );
};

export const SurchargeItem: FC<RouteComponentProps<{ id: string }>> = ({ match }) => {
  const fetch = useFetcher(
    async () => await api.getSurchargeById(Number.parseInt(match.params.id, 10)),
    [match.params.id],
  );

  return (
    <UserStateComponent>
      {fetch.data ? (
        <SurchargeContent refresh={fetch.refresh} surcharge={fetch.data} />
      ) : (
        <Fetcher inline result={fetch} />
      )}
    </UserStateComponent>
  );
};

function getProductDropdownOptions(
  ts: StringTranslator,
  surcharge: Surcharge,
  productId: number,
  refresh: () => void,
): DropdownItemProps[] {
  const out: DropdownItemProps[] = [
    {
      className: 'view-product',
      to: `/housekeeping/products/${productId}`,
      text: 'common.view_product',
    },
  ];

  if (!surcharge.deletedAt) {
    out.push({
      className: 'delete-product-surcharge',
      isDangerous: true,
      onClick: async () => {
        await api.deleteSurchargeProduct(productId, surcharge.id);
        toast.success(ts('removed_surcharge_from_product_productid', productId));
        refresh();
      },
      text: 'unassign',
    });
  }

  return out;
}
